import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { influencerResetPassword } from '../lib/influencer-api';
import { FaArrowLeft, FaCheckCircle, FaExclamationTriangle } from '../lib/icons';

export default function InfluencerResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing reset token. Please use the link from your email.');
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await influencerResetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/influencer', { replace: true }), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Reset Password — EatMeat Influencer Portal</title>
      </Helmet>
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
        <Link to="/influencer" className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 mb-6">
          <FaArrowLeft /> Back to login
        </Link>
        <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Choose a new password</h1>
        <p className="text-sm text-gray-500 mt-1">Pick a strong password. Minimum 6 characters.</p>

        {done ? (
          <div className="mt-6 px-4 py-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800 flex items-start gap-2">
            <FaCheckCircle className="mt-0.5 text-green-600 shrink-0" />
            <div>
              Password updated. Redirecting you to log in…
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">New password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Confirm password</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full inline-flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
